import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import config from './config';

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err, session) => {
        if (err) {
          setUser(null);
        } else if (session.isValid()) {
          setUser({
            username: currentUser.getUsername(),
            email: currentUser.getUsername(),
          });
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    setError(null);
    setLoading(true);

    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          setUser({
            username: cognitoUser.getUsername(),
            email: username,
          });
          setLoading(false);
          resolve(result);
        },
        onFailure: (err) => {
          setError(err.message || 'Login failed');
          setLoading(false);
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          // Handle new password requirement (first login after admin creates user)
          setError('NEW_PASSWORD_REQUIRED');
          setLoading(false);
          // Store cognitoUser for password change
          window.tempCognitoUser = cognitoUser;
          window.tempUserAttributes = userAttributes;
          reject({ code: 'NEW_PASSWORD_REQUIRED' });
        },
      });
    });
  };

  const completeNewPassword = async (newPassword) => {
    setError(null);
    setLoading(true);

    return new Promise((resolve, reject) => {
      const cognitoUser = window.tempCognitoUser;
      if (!cognitoUser) {
        setError('Session expired, please login again');
        setLoading(false);
        reject(new Error('No pending password change'));
        return;
      }

      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (result) => {
          setUser({
            username: cognitoUser.getUsername(),
            email: cognitoUser.getUsername(),
          });
          setLoading(false);
          window.tempCognitoUser = null;
          window.tempUserAttributes = null;
          resolve(result);
        },
        onFailure: (err) => {
          setError(err.message || 'Password change failed');
          setLoading(false);
          reject(err);
        },
      });
    });
  };

  const logout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    completeNewPassword,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
